import { useEffect } from "react";
import { useModal } from "@ebay/nice-modal-react";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import { Download, Edit } from "components/icons/generated";
import useUserGuideCertificates from "shared/UserGuides/useUserGuideCertificates";
import useUserGuideDownloadCertificate from "shared/UserGuides/useUserGuideDownloadCertificate";

import { showEditCertificateModal } from "../EditCertificateModal";
import CertificateDetailsDrawerCard from "./CertificateCard";

type CertificateDetailsDrawerProps = {
  certificateId: string;
  onClose?: () => void;
};

const CertificateDetailsDrawer = createDrawer(
  ({ certificateId, onClose }: CertificateDetailsDrawerProps) => {
    const modal = useModal();
    const { certificates, loading } = useUserGuideCertificates();
    const certificate = certificates.find((c) => c.id === certificateId);
    const { download, isDownloading } = useUserGuideDownloadCertificate(certificate, {
      detailsDrawer: true,
      mission: certificate?.groupID ?? undefined,
    });

    useEffect(() => {
      if (!loading && !certificate) modal.hide();
    }, [loading, certificate, modal]);

    if (!certificate) return null;

    const handleEdit = () =>
      showEditCertificateModal({
        certificateId: certificate.id,
        currentDisplayName: certificate.displayName,
      });

    return (
      <DrawerSimple onClose={onClose}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Certificate" />
          <Box align="center" gap="medium">
            <ButtonIcon variant="ghost" icon={Edit} onPress={handleEdit}>
              Edit
            </ButtonIcon>
            <DrawerCloseIcon />
          </Box>
        </DrawerHeader>
        <DrawerBody>
          <CertificateDetailsDrawerCard certificate={certificate} />
        </DrawerBody>
        <DrawerFooter gap="medium">
          <Button
            variant="primary"
            size="medium"
            startIcon={Download}
            onPress={download}
            loading={isDownloading}
          >
            Download as PNG
          </Button>
        </DrawerFooter>
      </DrawerSimple>
    );
  }
);

export const showCertificateDetailsDrawer = createDrawerTrigger(CertificateDetailsDrawer);
